import { COMPONENT_FACTORY_CONFIG_INPUT_COMPONENTS } from '@core/components/component-factory/component-factory-config-input.registry';
import type { ConfigComponentContract } from '@shared/utils/content-builder/config-component-contract';
import { computedResolvedConfig } from '@shared/utils/content-builder/block-config-computed-resolver';
import type { ShopDigitalMenuPageConfig } from '@front-platform/content-builder';
import { shopDigitalMenuPageResolveConfig } from '@front-platform/content-builder';
import { Subject } from 'rxjs';
import { Meta } from '@angular/platform-browser';
import { ActivatedRoute } from '@angular/router';
import { Component, OnDestroy, OnInit, input } from '@angular/core';
import { distinctUntilKeyChanged, take, takeUntil, tap } from 'rxjs/operators';

import { DoodShopModel } from '@core/models/shop.model';
import { DistributionModeValues } from '@config/values/order.values';

import { ShopsService } from '@core/services/shops/shops.service';
import { ShopStoreDispatcher } from '@common/dispatchers/shop.dispatcher';
import { NavigationHistoryService } from '@core/services/navigation-history/navigation-history.service';
export interface ShopDigitalMenuPageRuntimeConfig {
  'body'?: unknown[];
}

export type ShopDigitalMenuPageConfigInput = Partial<
  Omit<ShopDigitalMenuPageConfig, keyof ShopDigitalMenuPageRuntimeConfig> &
    ShopDigitalMenuPageRuntimeConfig
>;
export type ShopDigitalMenuPageResolvedConfig = Omit<
  ShopDigitalMenuPageConfig,
  keyof ShopDigitalMenuPageRuntimeConfig
> &
  Partial<ShopDigitalMenuPageRuntimeConfig>;
export interface ShopDigitalMenuPageContract extends ConfigComponentContract<
  ShopDigitalMenuPageConfigInput,
  ShopDigitalMenuPageResolvedConfig
> {}

@Component({
  selector: 'app-shop-digital-menu-page',
  templateUrl: './shop-digital-menu-page.component.html',
  standalone: false,
})
export class ShopDigitalMenuPageComponent
  implements OnInit, OnDestroy, ShopDigitalMenuPageContract
{
  readonly config = input<ShopDigitalMenuPageConfigInput>({});
  readonly resolvedConfig = computedResolvedConfig<
    ShopDigitalMenuPageConfig,
    ShopDigitalMenuPageRuntimeConfig
  >(shopDigitalMenuPageResolveConfig, () => ({ config: this.config() }));
  get body(): unknown[] {
    return this.resolvedConfig()['body'] as unknown as unknown[];
  }
  get metaDescription(): string | undefined {
    return this.resolvedConfig()['meta_description'] as unknown as string | undefined;
  }

  private readonly destroyed$ = new Subject<boolean>();
  // private shop?: IShop;

  constructor(
    private readonly meta: Meta,
    private shopDispatcher: ShopStoreDispatcher,
    private readonly shopsService: ShopsService,
    private readonly activatedRoute: ActivatedRoute,
    private readonly navigationHistoryService: NavigationHistoryService,
  ) {}

  ngOnInit(): void {
    this.navigationHistoryService.pushStateFromActivatedRouteSnapshot(
      'shop-digital-menu',
      this.activatedRoute.snapshot,
    );

    this.activatedRoute.params
      .pipe(
        takeUntil(this.destroyed$),
        distinctUntilKeyChanged('shopSlug'),
        tap(params => this.loadShop(params.shopSlug)),
      )
      .subscribe();

    if (this.metaDescription) {
      this.meta.updateTag({
        name: 'description',
        content: this.metaDescription,
      });
    }
  }

  ngOnDestroy(): void {
    // Leaving the page: clear the page-bound shop so visibility conditions stop matching.
    this.shopDispatcher.setCurrentPageShop(null);
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  loadShop(shopSlug: string): void {
    this.shopDispatcher.updateIsLoading(true);
    // Avoid leaking the previous shop while the new one is loading.
    this.shopDispatcher.setCurrentPageShop(null);
    this.shopsService
      .loadShopBySlug$(shopSlug, DistributionModeValues.EatIn)
      .pipe(
        takeUntil(this.destroyed$),
        take(1),
        tap(shop => this.shopLoaded(shop)),
      )
      .subscribe();
  }

  private shopLoaded(shop: DoodShopModel): void {
    // this.shop = shop;
    this.shopDispatcher.insertActive(shop);
    this.shopDispatcher.setCurrentPageShop(shop.id);
    this.shopDispatcher.updateIsLoading(false);
  }
}
COMPONENT_FACTORY_CONFIG_INPUT_COMPONENTS.set(ShopDigitalMenuPageComponent, { 'keyAliases': {} });
